// Frontend feature flag for the AI Dialer v1 surface.
//
// Mirrors the backend AI_DIALER_ENABLED gate. The flag is effectively true
// when EITHER:
//   • VITE_ENABLE_AI_DIALER === 'true' (build-time env), OR
//   • the auth'd tenant has features.aiDialerEnabled === true
//     (runtime — fetched from /api/tenant/features after login)
//
// Day 5 adds the runtime per-tenant override so we can enable AI Dialer for
// a single tenant in production without flipping a global env var.

import { useEffect, useState } from 'react';
import { apiGet } from './api.js';

export const AI_DIALER_BUILD_FLAG = import.meta.env.VITE_ENABLE_AI_DIALER === 'true';

// Per-session cache of the runtime tenant feature lookup.
let _runtimeFlag = null;
let _inflight = null;

export async function fetchTenantFeatures() {
  if (_runtimeFlag !== null) return _runtimeFlag;
  if (_inflight) return _inflight;
  _inflight = apiGet('/tenant/features')
    .then((r) => {
      _runtimeFlag = !!r?.features?.aiDialerEnabled;
      return _runtimeFlag;
    })
    .catch(() => {
      _runtimeFlag = false;
      return false;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function clearAiDialerCache() { _runtimeFlag = null; }

// Synchronous accessor — when the runtime fetch hasn't completed yet, falls
// back to the build flag. Routes that gate-render should prefer useAIDialerEnabled.
export function isAIDialerEnabled() {
  return AI_DIALER_BUILD_FLAG || _runtimeFlag === true;
}

// Back-compat for code that imports the constant. After the first runtime
// fetch resolves, isAIDialerEnabled() returns true; the constant export
// remains the build-time value (true if VITE flag is set).
export const AI_DIALER_ENABLED = AI_DIALER_BUILD_FLAG;

// React hook — returns { enabled, loading }. Triggers the fetch on first use
// and shares the cache across components.
export function useAIDialerEnabled() {
  const [enabled, setEnabled] = useState(AI_DIALER_BUILD_FLAG || _runtimeFlag === true);
  const [loading, setLoading] = useState(_runtimeFlag === null && !AI_DIALER_BUILD_FLAG);
  useEffect(() => {
    if (AI_DIALER_BUILD_FLAG) return; // build flag wins, no need to fetch
    if (_runtimeFlag !== null) {
      setEnabled(_runtimeFlag);
      setLoading(false);
      return;
    }
    let alive = true;
    fetchTenantFeatures().then((flag) => {
      if (!alive) return;
      setEnabled(!!flag);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);
  return { enabled, loading };
}
